import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerHeader from '../components/CustomerHeader';
import { supabase } from '../config/supabase';
import './CustomerOrders.css';

const CustomerOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (customer) {
      fetchOrders();
    }
  }, [customer]);

  const checkAuth = () => {
    try {
      const customerData = localStorage.getItem('customer');
      if (!customerData) {
        navigate('/login');
        return;
      }

      const customerObj = JSON.parse(customerData);
      setCustomer(customerObj);
    } catch (error) {
      console.error('Error loading customer data:', error);
      navigate('/login');
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      if (!customer || !customer.id) {
        setLoading(false);
        return;
      }

      // Fetch orders for this customer
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('customer_id', customer.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform orders for display
      const transformedOrders = (data || []).map((order, index) => {
        // Parse items if it's a string
        const orderItems = typeof order.items === 'string' 
          ? JSON.parse(order.items) 
          : order.items;

        // Map database status to display status
        const statusMap = {
          'pending': { text: 'Pending', color: '#F7B801', bg: '#FFF3CD' },
          'confirmed': { text: 'Confirmed', color: '#fe0000', bg: '#feecec' },
          'preparing': { text: 'Preparing', color: '#d40000', bg: '#feecec' },
          'ready': { text: 'Ready', color: '#ff3333', bg: '#feecec' },
          'out_for_delivery': { text: 'Out for Delivery', color: '#2D6A4F', bg: '#D1F2EB' },
          'delivered': { text: 'Delivered', color: '#2D6A4F', bg: '#D1F2EB' },
          'cancelled': { text: 'Cancelled', color: '#D62828', bg: '#FEE' }
        };

        const statusInfo = statusMap[order.status?.toLowerCase()] || statusMap['pending'];

        const orderDate = new Date(order.created_at);
        const formattedDate = orderDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric' 
        });
        const formattedTime = orderDate.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        });

        const itemCount = orderItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

        return {
          id: order.id,
          items: orderItems,
          itemCount,
          total: parseFloat(order.total || 0),
          status: statusInfo.text,
          statusColor: statusInfo.color,
          statusBg: statusInfo.bg,
          date: formattedDate,
          time: formattedTime,
          createdAt: order.created_at,
          deliveryAddress: order.delivery_address,
          paymentMethod: order.payment_method === 'cash' ? 'Cash on Delivery' : 'Card Payment',
          fullOrder: order
        };
      });

      setOrders(transformedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOrderClick = (order) => {
    navigate(`/order-confirmation/${order.id}`);
  };

  const getCartItemCount = () => {
    try {
      const cart = localStorage.getItem('restaurantCart');
      if (!cart) return 0;
      const cartItems = JSON.parse(cart);
      return cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
    } catch {
      return 0;
    }
  };

  // Filter orders by search query
  const filteredOrders = orders.filter(order => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.status.toLowerCase().includes(query) ||
      order.deliveryAddress.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="customer-orders-page">
        <CustomerHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          cartItemCount={getCartItemCount()}
          onCartClick={() => navigate('/cart')}
        />
        <div className="customer-orders-loading">
          <div className="customer-orders-loading-spinner">⏳</div>
          <p>Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-orders-page">
      <CustomerHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        cartItemCount={getCartItemCount()}
        onCartClick={() => navigate('/cart')}
      />
      
      <div className="customer-orders-main">
        <div className="customer-orders-container">
          <div className="customer-orders-header">
            <h1 className="customer-orders-title">My Orders</h1>
            <p className="customer-orders-subtitle">View all your past and current orders</p>
          </div>

          {orders.length === 0 ? (
            <div className="customer-orders-empty">
              <div className="customer-orders-empty-icon">📦</div>
              <h2 className="customer-orders-empty-title">No Orders Yet</h2>
              <p className="customer-orders-empty-text">
                You haven't placed any orders yet. Start ordering delicious food!
              </p>
              <button
                className="customer-orders-empty-btn"
                onClick={() => navigate('/menu')}
              >
                Browse Menu
              </button>
            </div>
          ) : (
            <>
              {searchQuery && (
                <div className="customer-orders-search-info">
                  Found {filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''}
                </div>
              )}

              <div className="customer-orders-list">
                {filteredOrders.map((order) => (
                  <div
                    key={order.id}
                    className="customer-orders-card"
                    onClick={() => handleOrderClick(order)}
                  >
                    <div className="customer-orders-card-header">
                      <div className="customer-orders-card-left">
                        <div className="customer-orders-date-time">
                          <span className="customer-orders-date">{order.date}</span>
                          <span className="customer-orders-time">{order.time}</span>
                        </div>
                      </div>
                      <div
                        className="customer-orders-status-badge"
                        style={{
                          backgroundColor: order.statusBg,
                          color: order.statusColor
                        }}
                      >
                        {order.status}
                      </div>
                    </div>

                    <div className="customer-orders-card-body">
                      <div className="customer-orders-items-info">
                        <div className="customer-orders-items-count">
                          {order.itemCount} {order.itemCount === 1 ? 'item' : 'items'}
                        </div>
                        <div className="customer-orders-items-list">
                          {order.items.slice(0, 3).map((item, index) => (
                            <span key={index} className="customer-orders-item-name">
                              {item.name || item}
                              {item.quantity > 1 && ` (x${item.quantity})`}
                            </span>
                          ))}
                          {order.items.length > 3 && (
                            <span className="customer-orders-item-more">
                              +{order.items.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="customer-orders-delivery-info">
                        <div className="customer-orders-delivery-label">Delivery Address:</div>
                        <div className="customer-orders-delivery-address">
                          {order.deliveryAddress}
                        </div>
                      </div>

                      <div className="customer-orders-payment-info">
                        <span className="customer-orders-payment-method">{order.paymentMethod}</span>
                      </div>
                    </div>

                    <div className="customer-orders-card-footer">
                      <div className="customer-orders-total">
                        <span className="customer-orders-total-label">Total:</span>
                        <span className="customer-orders-total-amount">Rs. {order.total.toFixed(2)}</span>
                      </div>
                      <button
                        className="customer-orders-view-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOrderClick(order);
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerOrders;

